import * as React from 'react';
import _ from 'lodash';
import type { CSSSize } from '../../../types';
import type { ReactRef } from '../../../modules/react-utils';
import cn from '~modules/cn';

import './resizable.scss';

export enum ResizeDirection {
    Top = 'top',
    Right = 'right',
    Bottom = 'bottom',
    Left = 'left'
}

enum ResizeMultipleDirection {
    TopRight = 'top-right',
    BottomRight = 'bottom-right',
    TopLeft = 'top-left',
    BottomLeft = 'bottom-left'
}

type ResizeProperties = ['width' | 'height', 'clientX' | 'clientY', number];

const RESIZE_ATTRIBUTE: { [K in ResizeDirection | ResizeMultipleDirection]?: [ResizeProperties, ResizeProperties?] } = {
    [ResizeDirection.Top]: [['height', 'clientY', -1]],
    [ResizeDirection.Right]: [['width', 'clientX', 1]],
    [ResizeDirection.Bottom]: [['height', 'clientY', 1]],
    [ResizeDirection.Left]: [['width', 'clientX', -1]]
};

_.forEach(ResizeMultipleDirection, (direction) => {
    const [firstKey, secondKey] = direction.split('-') as [ResizeDirection, ResizeDirection];
    RESIZE_ATTRIBUTE[direction] = [RESIZE_ATTRIBUTE[firstKey]![0], RESIZE_ATTRIBUTE[secondKey]![0]];
});

type ResizablePrivateProps = Omit<React.ComponentPropsWithoutRef<'div'>, 'onResize'> & {
    className?: string;
    setRef?: ReactRef<HTMLDivElement>;
    direction: ResizeDirection[];
    minWidth: number;
    maxWidth: number;
    minHeight: number;
    maxHeight: number;
    initialWidth: (() => number) | number | string;
    initialHeight: (() => number) | number | string;
    onResize?: (newSize: Partial<CSSSize>) => void;
    style?: React.CSSProperties;
};

export type ResizableProps = Partial<ResizablePrivateProps>;

type ResizeState = {
    resizing: boolean;
};

export default class Resizable extends React.Component<ResizablePrivateProps, ResizeState> {
    static defaultProps = {
        direction: Object.values(ResizeDirection),
        minWidth: 0,
        maxWidth: window.innerWidth,
        minHeight: 0,
        maxHeight: window.innerHeight,
        initialWidth: '',
        initialHeight: ''
    };

    state: ResizeState = {
        resizing: false
    };

    resizingDirection: ResizeDirection | ResizeMultipleDirection | undefined;

    resizingInfo: { clientX: number; clientY: number; width: number; height: number } | null = null;

    baseEl = React.createRef() as React.MutableRefObject<HTMLDivElement>;

    setRef = (newRef: HTMLDivElement): void => {
        this.baseEl.current = newRef;
        if (typeof this.props.setRef === 'function') {
            this.props.setRef(newRef);
        } else if (this.props.setRef) {
            this.props.setRef.current = newRef;
        }
    };

    getDirections(): (ResizeDirection | ResizeMultipleDirection)[] {
        const { direction } = this.props;
        const topBottom = [ResizeDirection.Top, ResizeDirection.Bottom];

        return _.flatMap(direction, (currentDirection) => {
            const additionalDirections = [];
            if (topBottom.includes(currentDirection)) {
                if (direction?.includes(ResizeDirection.Left)) {
                    additionalDirections.push(ResizeMultipleDirection[`${_.capitalize(currentDirection)}Left` as keyof typeof ResizeMultipleDirection]);
                }
                if (direction?.includes(ResizeDirection.Right)) {
                    additionalDirections.push(ResizeMultipleDirection[`${_.capitalize(currentDirection)}Right` as keyof typeof ResizeMultipleDirection]);
                }
            }
            return [currentDirection, ...additionalDirections];
        });
    }

    onMouseDown = (e: React.MouseEvent): void => {
        if (this.resizingDirection || !this.baseEl.current) {
            return;
        }
        const { clientX, clientY } = e;
        const style = window.getComputedStyle(this.baseEl.current);
        const width = parseInt(style.width, 10);
        const height = parseInt(style.height, 10);
        document.addEventListener('mousemove', this.onMouseMove);
        document.addEventListener('mouseup', this.onMouseUp);

        this.resizingDirection = (e.target as HTMLElement).getAttribute('data-direction') as ResizeDirection;
        this.resizingInfo = { clientX, clientY, width, height };

        document.body.classList.add('resizing-active', `resizing-active-${this.resizingDirection}`);

        this.setState({ resizing: true });
    };

    onMouseMove = (e: MouseEvent): void => {
        if (!this.baseEl.current) {
            return;
        }

        const resizeResponse: { [K in 'width' | 'height']?: number } = {};

        RESIZE_ATTRIBUTE[this.resizingDirection!]!.forEach((resizeAttribute) => {
            const [attribute, startAttribute, swapOperands] = resizeAttribute!;
            const direction = this.resizingInfo![startAttribute] > e[startAttribute] ? -1 : 1;
            const delta = Math.abs(this.resizingInfo![startAttribute] - e[startAttribute]);
            let newValue = this.resizingInfo![attribute] + direction * swapOperands * delta;

            const minMaxValueName = _.capitalize(attribute) as 'Width' | 'Height';
            const min = this.props[`min${minMaxValueName}`];
            const max = this.props[`max${minMaxValueName}`];

            if (newValue < min) {
                newValue = min;
            }

            if (newValue > max) {
                newValue = max;
            }

            this.baseEl.current.style[attribute] = `${newValue}px`;
            resizeResponse[attribute] = newValue;
        });

        this.props.onResize?.(resizeResponse);
    };

    onMouseUp = (): void => {
        document.body.classList.remove('resizing-active', `resizing-active-${this.resizingDirection}`);
        this.resizingDirection = undefined;
        this.resizingInfo = null;
        document.removeEventListener('mousemove', this.onMouseMove);
        document.removeEventListener('mouseup', this.onMouseUp);
        this.setState({ resizing: false });
    };

    render(): JSX.Element {
        const className = cn('resizable-box', this.props.className, {
            'resizable-box-resizing': this.state.resizing
        });
        const props = _.omit(this.props, [
            'setRef',
            'className',
            'onResize',
            'direction',
            'minWidth',
            'maxWidth',
            'minHeight',
            'maxHeight',
            'initialWidth',
            'initialHeight'
        ]);
        return (
            <div
                {...props}
                className={className}
                ref={this.setRef}
                style={{
                    width: _.result(this.props, 'initialWidth'),
                    height: _.result(this.props, 'initialHeight'),
                    ...this.props.style
                }}
            >
                {this.props.children}
                {this.getDirections().map((direction) => (
                    <div
                        key={direction}
                        className={`resizable-drag-handle resizable-drag-handle-${direction}`}
                        data-direction={direction}
                        onMouseDown={this.onMouseDown}
                    />
                ))}
            </div>
        );
    }
}
